import React, { FC, useMemo, useState } from 'react';
import { Button, Checkbox, Input, message, Modal, Select, Space } from 'antd';
import Column from 'antd/lib/table/Column';
import { useMutation, useQuery } from 'react-query';
import {
  AdditionalDeviceType,
  BusinessPartnerAdditionalDevice,
  BusinessPartnersAPI,
} from 'api/BusinessPartnersAPI';
import { QueryKey } from 'api/queryKey';
import { NumberedTable } from 'common/components/NumberedTable';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useRole } from 'common/hooks/useRole';
import { commonPaginationConfig, initialPageParams } from 'models/page';
import { purge } from 'lib/ramda/purge';
import styled from 'styled-components';

const StyledModal = styled(Modal)`
  width: 70% !important;
`;

const mapDeviceTypeToLabel = (deviceType?: string | null) => {
  if (deviceType === 'OXIMETER') {
    return 'OXY';
  }

  if (deviceType === 'BP_MONITOR') {
    return 'BPM';
  }

  return deviceType !== null && deviceType !== undefined && deviceType !== ''
    ? deviceType
    : '--';
};

interface AssignAdditionalDevicesToPartnerModalProps {
  businessPartnerId: number;
  visible: boolean;
  onCancel: () => void;
  onAssigned?: () => void;
}

interface AssignAdditionalDevicesFilters {
  serialNumber?: string;
  type?: AdditionalDeviceType;
}

const { Search } = Input;

export const AssignAdditionalDevicesToPartnerModal: FC<AssignAdditionalDevicesToPartnerModalProps> = ({
  businessPartnerId,
  onAssigned,
  visible,
  onCancel,
  ...modalProps
}) => {
  const intlMessage = useFormatMessage();
  const { isSuperAdmin } = useRole();

  const [pageMetaData, setPageMetaData] = useState(initialPageParams);
  const [filters, setFilters] = useState<AssignAdditionalDevicesFilters>({});
  const [
    selectedAdditionalDevicesIds,
    setSelectedAdditionalDevicesIds,
  ] = useState<number[]>([]);
  const [isAssigningSelected, setIsAssigningSelected] = useState(false);

  const [
    assignAdditionalDevice,
    { isLoading: isAssigningSingle },
  ] = useMutation(BusinessPartnersAPI.assignAdditionalDeviceToPartner);

  const { data, isFetching, refetch } = useQuery(
    [
      QueryKey.SelectAdditionalDevicesToAssignList,
      businessPartnerId,
      pageMetaData,
      filters,
      visible,
    ],
    () =>
      BusinessPartnersAPI.fetchAdditionalDevicesForSuperAdmin({
        ...pageMetaData,
        ...purge(filters),
      }),
    {
      enabled: isSuperAdmin && !!visible,
      keepPreviousData: true,
    }
  );

  const rows = (data?.content ?? []).filter(
    (additionalDevice) =>
      additionalDevice.partnerName === null ||
      additionalDevice.partnerName === ''
  );
  const currentPageAdditionalDeviceIds = useMemo(
    () => rows.map((row) => row.id),
    [rows]
  );
  const isAllOnCurrentPageSelected =
    currentPageAdditionalDeviceIds.length > 0 &&
    currentPageAdditionalDeviceIds.every((id) =>
      selectedAdditionalDevicesIds.includes(id)
    );

  const isAssigning = isAssigningSingle || isAssigningSelected;

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  const toggleSelected = (additionalDeviceId: number, checked: boolean) => {
    setSelectedAdditionalDevicesIds((currentIds) => {
      if (checked) {
        return currentIds.includes(additionalDeviceId)
          ? currentIds
          : [...currentIds, additionalDeviceId];
      }

      return currentIds.filter((id) => id !== additionalDeviceId);
    });
  };

  const toggleSelectAllOnCurrentPage = () => {
    setSelectedAdditionalDevicesIds((currentIds) => {
      if (isAllOnCurrentPageSelected) {
        return currentIds.filter(
          (id) => !currentPageAdditionalDeviceIds.includes(id)
        );
      }

      return Array.from(
        new Set([...currentIds, ...currentPageAdditionalDeviceIds])
      );
    });
  };

  const resetAndClose = () => {
    setSelectedAdditionalDevicesIds([]);
    onCancel();
  };

  const onSingleAssign = (
    additionalDevice: BusinessPartnerAdditionalDevice
  ) => {
    assignAdditionalDevice(
      {
        additionalDeviceId: additionalDevice.id,
        partnerId: businessPartnerId,
      },
      {
        onSuccess: () => {
          message.success(intlMessage({ id: 'devices.assignSelected' }));
          setSelectedAdditionalDevicesIds([]);
          onAssigned && onAssigned();
          refetch();
        },
      }
    );
  };

  const onAssignSelected = async () => {
    if (selectedAdditionalDevicesIds.length === 0 || isAssigning) {
      return;
    }

    setIsAssigningSelected(true);

    try {
      await Promise.all(
        selectedAdditionalDevicesIds.map((additionalDeviceId) =>
          BusinessPartnersAPI.assignAdditionalDeviceToPartner({
            additionalDeviceId,
            partnerId: businessPartnerId,
          })
        )
      );

      message.success(intlMessage({ id: 'devices.assignSelected' }));
      setSelectedAdditionalDevicesIds([]);
      onAssigned && onAssigned();
      refetch();
    } finally {
      setIsAssigningSelected(false);
    }
  };

  return (
    <StyledModal
      visible={visible}
      centered={true}
      maskClosable={false}
      destroyOnClose={true}
      title={intlMessage({ id: 'devices.assignToPartner' })}
      onCancel={resetAndClose}
      footer={
        <Space>
          <Button onClick={resetAndClose} disabled={isAssigning}>
            {intlMessage({ id: 'cancel' })}
          </Button>
          {selectedAdditionalDevicesIds.length > 0 && (
            <Button
              type="primary"
              loading={isAssigning}
              onClick={onAssignSelected}
            >
              {intlMessage({ id: 'devices.assignSelected' })}
            </Button>
          )}
        </Space>
      }
      {...modalProps}
    >
      <Search
        placeholder={intlMessage({ id: 'devices.serialNumber.placeholder' })}
        onSearch={(value) => {
          setPageMetaData((prev) => ({ ...prev, page: 1 }));
          setFilters((prev) => ({
            ...prev,
            serialNumber: value,
          }));
        }}
        style={{ width: '100%', marginBottom: '1rem' }}
      />

      <h4>{intlMessage({ id: 'deviceType' })}</h4>
      <Select
        allowClear
        value={filters.type}
        onChange={(value) => {
          setPageMetaData((prev) => ({ ...prev, page: 1 }));
          setFilters((prev) => ({
            ...prev,
            type: value as AdditionalDeviceType | undefined,
          }));
        }}
        style={{ width: '100%', marginBottom: '1rem' }}
        placeholder={intlMessage({ id: 'deviceType' })}
      >
        <Select.Option value="OXIMETER">OXY</Select.Option>
        <Select.Option value="BP_MONITOR">BPM</Select.Option>
      </Select>

      <NumberedTable
        rowKey="id"
        size="middle"
        loading={isFetching || isAssigning}
        dataSource={rows}
        pagination={paginationConfig}
      >
        <Column
          title={intlMessage({ id: 'deviceSerialNumber' })}
          dataIndex="serialNumber"
          render={(value) => (value !== null && value !== '' ? value : '--')}
        />
        <Column
          title={intlMessage({ id: 'deviceType' })}
          dataIndex="deviceType"
          render={(value) => mapDeviceTypeToLabel(value)}
        />
        <Column
          title={
            <Button type="link" onClick={toggleSelectAllOnCurrentPage}>
              Select all
            </Button>
          }
          align="center"
          width={150}
          render={(record: BusinessPartnerAdditionalDevice) => (
            <Checkbox
              checked={selectedAdditionalDevicesIds.includes(record.id)}
              onChange={(event) =>
                toggleSelected(record.id, event.target.checked)
              }
            />
          )}
        />
        <Column
          title={null}
          align="right"
          width={150}
          render={(record: BusinessPartnerAdditionalDevice) => (
            <Button
              type="primary"
              loading={isAssigning}
              onClick={() => onSingleAssign(record)}
            >
              {intlMessage({ id: 'assign' })}
            </Button>
          )}
        />
      </NumberedTable>
    </StyledModal>
  );
};
